import React, { useState, useEffect } from 'react';
import { userService, organizationService, settingsService, notificationService } from '../services/api';
import { Organization } from '../types';
import { useAuth } from '../context/AuthContext';
import { useEnvironmentMode } from '../services/productionModeService';
import { PurgeMockDataModal } from '../components/admin/PurgeMockDataModal';

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
  requestedRole?: string;
  idType?: string;
  idNumber?: string;
  idCardUrl?: string;
  organizationId?: string;
  certifications?: string[];
  isActive: boolean;
  createdAt: string;
}

const STAFF_ROLES = [
  'super_admin',
  'admin',
  'sales',
  'accounts',
  'customer_support',
  'staff',
  'master_admin',
  // legacy compatibility
  'field_staff',
  'state_manager',
  'site_manager',
  'team_lead',
  'site_engineer',
  'storekeeper'
];
const ALL_ROLES = [
  'super_admin',
  'master_admin',
  'admin',
  'state_manager',
  'site_manager',
  'team_lead',
  'site_engineer',
  'storekeeper',
  'field_staff',
  'staff',
  'technician',
  'sales',
  'accounts',
  'customer_support',
  'owner',
  'pending'
];

export const AdminPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { isProduction } = useEnvironmentMode();
  const [isPurgeModalOpen, setIsPurgeModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'staff' | 'technicians' | 'owners' | 'tenants' | 'settings'>('staff');
  
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [bankDetails, setBankDetails] = useState({ bankName: '', accountName: '', accountNumber: '', instructions: '' });
  
  // Modal for Subscription / Messaging
  const [managingOrg, setManagingOrg] = useState<Organization | null>(null);
  const [msgTitle, setMsgTitle] = useState('');
  const [msgBody, setMsgBody] = useState('');

  const [createFormData, setCreateFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'field_staff',
    organizationId: '',
    certifications: '',
  });

  const [editFormData, setEditFormData] = useState({
    role: 'staff',
    organizationId: '',
    certifications: '',
    isActive: true,
  });

  useEffect(() => {
    loadUsers();
    loadOrganizations();
    loadBankDetails();
  }, []);

  const loadBankDetails = async () => {
    try {
      const res = await settingsService.getBankDetails();
      if (res.data) setBankDetails(res.data);
    } catch (err) {}
  };

  const loadOrganizations = async () => {
    try {
      const response = await organizationService.getOrganizations();
      setOrganizations(response.data);
    } catch (err) {
      console.error('Failed to load organizations', err);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers();
      setUsers(response.data);
      setError('');
    } catch (err: any) {
      setError(err?.message || err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCreateFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setCreateLoading(true);

    try {
      const targetOrg = createFormData.organizationId.trim() || currentUser?.organizationId || '';
      await userService.createUser({
        ...createFormData,
        organizationId: targetOrg,
        certifications: createFormData.certifications.split(',').map(c => c.trim()).filter(c => c !== ''),
      });
      setSuccess(`User ${createFormData.name} created successfully`);
      setCreateFormData({
        name: '',
        email: '',
        password: '',
        role: 'field_staff',
        organizationId: '',
        certifications: '',
      });
      setShowCreateForm(false);
      loadUsers();
    } catch (err: any) {
      setError(err?.message || err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      role: user.role && user.role !== 'pending' ? user.role : (user.requestedRole || 'field_staff'),
      organizationId: user.organizationId || currentUser?.organizationId || '',
      certifications: user.certifications ? user.certifications.join(', ') : '',
      isActive: user.isActive !== undefined ? user.isActive : true,
    });
    setEditError('');
  };

  const handleSaveEditedUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setEditError('');
    setEditLoading(true);

    try {
      const targetOrg = editFormData.organizationId.trim() || currentUser?.organizationId || null;
      await userService.updateUserRole(editingUser._id, {
        role: editFormData.role,
        organizationId: targetOrg,
        certifications: editFormData.certifications.split(',').map(c => c.trim()).filter(c => c !== ''),
        isActive: editFormData.isActive,
        requestedRole: null, // Clear pending flag upon assignment/promotion
      });
      setSuccess(`Role updated: ${editingUser.name} is now ${editFormData.role.replace(/_/g, ' ').toUpperCase()}`);
      setEditingUser(null);
      loadUsers();
    } catch (err: any) {
      console.error('Failed to update staff role:', err);
      setEditError(err?.message || 'Failed to update staff role');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await userService.deleteUser(userId);
      setSuccess('User deleted successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleApprove = async (user: User) => {
    if (!window.confirm(`Approve ${user.name} as ${user.requestedRole || 'technician'}?`)) return;
    
    try {
      await userService.updateUserRole(user._id, {
        role: user.requestedRole || 'technician',
        requestedRole: null,
        isActive: true, // Mark them as active upon approval
        organizationId: user.organizationId || currentUser?.organizationId || null,
      });
      setSuccess(`User ${user.name} approved successfully`);
      loadUsers();
    } catch (err: any) {
      setError(err?.message || 'Failed to approve user');
    }
  };

  const handlePromoteToSuperAdmin = async (user: User) => {
    if (!window.confirm(`Promote ${user.name} (${user.email}) to SUPER ADMIN?`)) return;
    
    try {
      await userService.updateUserRole(user._id, {
        role: 'super_admin',
        isActive: true,
        requestedRole: null,
      });
      setSuccess(`User ${user.name} is now a SUPER ADMIN!`);
      if (editingUser?._id === user._id) {
        setEditingUser(null);
      }
      loadUsers();
    } catch (err: any) {
      setError(err?.message || 'Failed to promote user to super admin');
    }
  };

  const handleApproveTenant = async (org: Organization) => {
    if (!window.confirm(`Approve tenant ${org.companyName}?`)) return;
    
    try {
      await organizationService.approveOrganization(org.id, org.adminUserId);
      setSuccess(`Tenant ${org.companyName} approved successfully`);
      loadOrganizations();
      loadUsers(); // Refresh users to reflect admin's new role
    } catch (err: any) {
      setError('Failed to approve tenant');
    }
  };

  const handleUpdateBankDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await settingsService.updateBankDetails(bankDetails);
      setSuccess('Bank details updated successfully!');
    } catch (err: any) {
      setError('Failed to update bank details');
    }
  };

  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!managingOrg) return;
    try {
      await notificationService.sendNotification(managingOrg.id, msgTitle, msgBody, 'warning');
      setSuccess('Message sent to Master Admin');
      setMsgTitle('');
      setMsgBody('');
      setManagingOrg(null);
    } catch (err) {
      setError('Failed to send message');
    }
  };

  const handleToggleSubscription = async (org: Organization) => {
    const newStatus = org.subscriptionStatus === 'defaulted' ? 'active' : 'defaulted';
    try {
      await organizationService.updateSubscription(org.id, { subscriptionStatus: newStatus });
      setSuccess(`Subscription status changed to ${newStatus}`);
      loadOrganizations();
    } catch (err) {
      setError('Failed to change status');
    }
  };

  // --- Per-tab user filters ---
  const staffUsers = users.filter(u =>
    STAFF_ROLES.includes(u.role) || (u.role === 'pending' && (!u.requestedRole || STAFF_ROLES.includes(u.requestedRole)))
  );
  const technicianUsers = users.filter(u =>
    u.role === 'technician' || (u.role === 'pending' && u.requestedRole === 'technician')
  );
  const ownerUsers = users.filter(u =>
    u.role === 'owner' || (u.role === 'pending' && u.requestedRole === 'owner')
  );

  const activeTabUsers = activeTab === 'staff' ? staffUsers
    : activeTab === 'technicians' ? technicianUsers
    : activeTab === 'owners' ? ownerUsers
    : [];

  const filteredUsers = activeTabUsers.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !filterRole || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6 min-w-0 max-w-full overflow-hidden">
      <div className="max-w-6xl mx-auto min-w-0 max-w-full">
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-end gap-4 min-w-0 max-w-full">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2.5 sm:gap-3">
              <span>Platform Management</span>
              <button onClick={() => { loadUsers(); loadOrganizations(); }} className="p-1.5 sm:p-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full transition-colors" title="Refresh Data">
                <svg className="w-4 sm:w-5 h-4 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              </button>
            </h1>
            <p className="text-gray-600 text-xs sm:text-sm mt-1 sm:mt-2">Onboard tenants and assign roles to VERISURE staff</p>
          </div>
          <div className="flex overflow-x-auto max-w-full pb-1 scrollbar-none touch-pan-x gap-1 bg-white rounded-xl shadow-sm border border-gray-200 p-1 w-full md:w-auto shrink-0">
            <button
              onClick={() => { setActiveTab('staff'); setFilterRole(''); }}
              className={`px-3 sm:px-4 py-2 rounded-lg font-semibold transition-colors text-xs sm:text-sm whitespace-nowrap shrink-0 ${activeTab === 'staff' ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              Staff &amp; Admins
              {staffUsers.filter(u => u.role === 'pending').length > 0 && (
                <span className="ml-1.5 bg-yellow-500 text-white text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full">
                  {staffUsers.filter(u => u.role === 'pending').length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('technicians'); setFilterRole(''); }}
              className={`px-3 sm:px-4 py-2 rounded-lg font-semibold transition-colors text-xs sm:text-sm whitespace-nowrap shrink-0 ${activeTab === 'technicians' ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              Technicians
              {technicianUsers.filter(u => u.role === 'pending').length > 0 && (
                <span className="ml-1.5 bg-yellow-500 text-white text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full">
                  {technicianUsers.filter(u => u.role === 'pending').length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('owners'); setFilterRole(''); }}
              className={`px-3 sm:px-4 py-2 rounded-lg font-semibold transition-colors text-xs sm:text-sm whitespace-nowrap shrink-0 ${activeTab === 'owners' ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              Owners
              {ownerUsers.filter(u => u.role === 'pending').length > 0 && (
                <span className="ml-1.5 bg-yellow-500 text-white text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full">
                  {ownerUsers.filter(u => u.role === 'pending').length}
                </span>
              )}
            </button>
            {currentUser?.role === 'super_admin' && (
              <button
                onClick={() => setActiveTab('tenants')}
                className={`px-3 sm:px-4 py-2 rounded-lg font-semibold transition-colors text-xs sm:text-sm whitespace-nowrap shrink-0 ${activeTab === 'tenants' ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                Tenants
                {organizations.filter(o => o.status === 'pending').length > 0 && (
                  <span className="ml-1.5 bg-red-500 text-white text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full">
                    {organizations.filter(o => o.status === 'pending').length}
                  </span>
                )}
              </button>
            )}
            {currentUser?.role === 'super_admin' && (
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-3 sm:px-4 py-2 rounded-lg font-semibold transition-colors text-xs sm:text-sm whitespace-nowrap shrink-0 ${activeTab === 'settings' ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                Settings
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg border border-red-200">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg border border-green-200">
            {success}
          </div>
        )}

        {(activeTab === 'staff' || activeTab === 'technicians' || activeTab === 'owners') ? (
          <>
            {/* --- Stat Cards --- */}
            {activeTab === 'staff' && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-sm p-3.5 sm:p-5 border border-gray-100">
                  <div className="text-2xl sm:text-3xl font-bold text-blue-600">{staffUsers.length}</div>
                  <div className="text-gray-600 text-xs sm:text-sm font-medium mt-0.5">Total Staff</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-3.5 sm:p-5 border-l-4 border-yellow-500 cursor-pointer hover:bg-yellow-50" onClick={() => setFilterRole('pending')}>
                  <div className="text-2xl sm:text-3xl font-bold text-yellow-600">{staffUsers.filter(u => u.role === 'pending').length}</div>
                  <div className="text-gray-600 text-xs sm:text-sm font-semibold mt-0.5">Pending Approvals</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-3.5 sm:p-5 border border-gray-100">
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-600">{staffUsers.filter(u => ['sales', 'accounts'].includes(u.role)).length}</div>
                  <div className="text-gray-600 text-xs sm:text-sm font-medium mt-0.5">Sales &amp; Accounts</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-3.5 sm:p-5 border border-gray-100">
                  <div className="text-2xl sm:text-3xl font-bold text-purple-600">{staffUsers.filter(u => ['customer_support', 'admin', 'super_admin', 'staff'].includes(u.role)).length}</div>
                  <div className="text-gray-600 text-xs sm:text-sm font-medium mt-0.5">Admin &amp; Support</div>
                </div>
              </div>
            )}
            {activeTab === 'technicians' && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 sm:gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-sm p-3.5 sm:p-5 border border-gray-100">
                  <div className="text-2xl sm:text-3xl font-bold text-indigo-600">{technicianUsers.length}</div>
                  <div className="text-gray-600 text-xs sm:text-sm font-medium mt-0.5">Total Technicians</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-3.5 sm:p-5 border-l-4 border-yellow-500 cursor-pointer hover:bg-yellow-50" onClick={() => setFilterRole('pending')}>
                  <div className="text-2xl sm:text-3xl font-bold text-yellow-600">{technicianUsers.filter(u => u.role === 'pending').length}</div>
                  <div className="text-gray-600 text-xs sm:text-sm font-semibold mt-0.5">Awaiting Approval</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-3.5 sm:p-5 border border-gray-100 col-span-2 md:col-span-1">
                  <div className="text-2xl sm:text-3xl font-bold text-green-600">{technicianUsers.filter(u => u.role === 'technician').length}</div>
                  <div className="text-gray-600 text-xs sm:text-sm font-medium mt-0.5">Active Technicians</div>
                </div>
              </div>
            )}
            {activeTab === 'owners' && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 sm:gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-sm p-3.5 sm:p-5 border border-gray-100">
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-600">{ownerUsers.length}</div>
                  <div className="text-gray-600 text-xs sm:text-sm font-medium mt-0.5">Total Owners</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-3.5 sm:p-5 border-l-4 border-yellow-500 cursor-pointer hover:bg-yellow-50" onClick={() => setFilterRole('pending')}>
                  <div className="text-2xl sm:text-3xl font-bold text-yellow-600">{ownerUsers.filter(u => u.role === 'pending').length}</div>
                  <div className="text-gray-600 text-xs sm:text-sm font-semibold mt-0.5">Awaiting Approval</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-3.5 sm:p-5 border border-gray-100 col-span-2 md:col-span-1">
                  <div className="text-2xl sm:text-3xl font-bold text-green-600">{ownerUsers.filter(u => u.role === 'owner').length}</div>
                  <div className="text-gray-600 text-xs sm:text-sm font-medium mt-0.5">Active Owners</div>
                </div>
              </div>
            )}

        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="mb-6 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition shadow-sm flex items-center gap-2"
        >
          {showCreateForm ? '✕ Close Form' : '+ Add New Staff / User'}
        </button>

        {showCreateForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border-2 border-blue-500 animate-in fade-in duration-150">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Add New Staff / User
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Directly provision an account and assign their operational role</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={createFormData.name}
                    onChange={handleCreateInputChange}
                    placeholder="e.g. John Doe"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={createFormData.email}
                    onChange={handleCreateInputChange}
                    placeholder="john@verisure.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Temporary Password</label>
                <input
                  type="password"
                  name="password"
                  value={createFormData.password}
                  onChange={handleCreateInputChange}
                  placeholder="Minimum 6 characters"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Assign Operational Role
                  </label>
                  <select
                    name="role"
                    value={createFormData.role}
                    onChange={handleCreateInputChange}
                    className="w-full px-4 py-2 border-2 border-blue-400 bg-blue-50 text-gray-900 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <optgroup label="⭐ Executive & Platform Administration">
                      {currentUser?.role === 'super_admin' && (
                        <option value="super_admin">SUPER ADMIN (Full Platform Control)</option>
                      )}
                      <option value="master_admin">MASTER ADMIN (Tenant / Company Owner)</option>
                      <option value="admin">ADMIN (Platform Administrator)</option>
                    </optgroup>
                    <optgroup label="🏢 Field Operations & Facilities">
                      <option value="state_manager">STATE MANAGER (Regional Operations Lead)</option>
                      <option value="site_manager">SITE MANAGER (Site Supervisor)</option>
                      <option value="team_lead">TEAM LEAD (Field Team Leader)</option>
                      <option value="site_engineer">SITE ENGINEER (Technical & Maintenance)</option>
                      <option value="storekeeper">STOREKEEPER (Inventory & Asset Custodian)</option>
                      <option value="field_staff">FIELD STAFF (Operational / On-Site)</option>
                      <option value="staff">STAFF (General Office / Staff)</option>
                    </optgroup>
                    <optgroup label="🔧 Technical & Hardware">
                      <option value="technician">VERIFIED TECHNICIAN (Forensic Ledger & Repairs)</option>
                    </optgroup>
                    <optgroup label="💼 Commercial & Customer Care">
                      <option value="sales">SALES (Business Development)</option>
                      <option value="accounts">ACCOUNTS (Finance & Billing)</option>
                      <option value="customer_support">CUSTOMER SUPPORT (Helpdesk & Service)</option>
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Organization ID</label>
                  <input
                    type="text"
                    name="organizationId"
                    value={createFormData.organizationId}
                    onChange={handleCreateInputChange}
                    placeholder={currentUser?.organizationId || "e.g. verisure_global"}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Certifications (ABAC)</label>
                <input
                  type="text"
                  name="certifications"
                  value={createFormData.certifications}
                  onChange={handleCreateInputChange}
                  placeholder="e.g. electrical, telecom, hvac"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Comma separated list of item categories this technician or engineer can service.</p>
              </div>

              <div className="flex gap-4 pt-2">
                <button
                  type="submit"
                  disabled={createLoading}
                  className="bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 font-bold shadow-sm disabled:opacity-50 flex items-center gap-2"
                >
                  {createLoading ? 'Creating User...' : 'Create User'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="bg-gray-400 text-white px-6 py-2.5 rounded-lg hover:bg-gray-500 font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Roles</option>
                {ALL_ROLES.map(role => (
                  <option key={role} value={role}>{role.replace(/_/g, ' ').toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="p-6 text-center text-gray-600">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-6 text-center text-gray-600">
              {activeTab === 'technicians' ? 'No technicians found. New technician registrations will appear here.' :
               activeTab === 'owners' ? 'No equipment owners found. New owner registrations will appear here.' :
               'No users found.'}
            </div>
          ) : (
            <div className="w-full max-w-full min-w-0 overflow-x-auto mobile-table-scroll-container rounded-b-xl">
              <table className="w-full min-w-[700px] text-left border-collapse">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Name</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Email</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Role</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Req. Role</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">ID Info</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Org ID</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user._id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{user.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          user.role === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          user.role === 'super_admin' ? 'bg-purple-100 text-purple-800 font-bold' :
                          user.role === 'admin' ? 'bg-blue-100 text-blue-800 font-semibold' :
                          user.role === 'sales' ? 'bg-emerald-100 text-emerald-800' :
                          user.role === 'accounts' ? 'bg-amber-100 text-amber-800' :
                          user.role === 'customer_support' ? 'bg-cyan-100 text-cyan-800' :
                          user.role === 'technician' ? 'bg-indigo-100 text-indigo-800' :
                          user.role === 'owner' ? 'bg-teal-100 text-teal-800' :
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {user.role.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{user.requestedRole ? user.requestedRole.replace(/_/g, ' ').toUpperCase() : '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {user.idType && user.idNumber ? (
                          <div className="flex flex-col gap-1">
                            <span className="font-semibold text-xs">{user.idType}</span>
                            <span className="font-mono text-xs">{user.idNumber}</span>
                            {user.idCardUrl && (
                              <button
                                onClick={() => setPreviewImage(user.idCardUrl!)}
                                className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 mt-1 self-start"
                              >
                                View ID Card
                              </button>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {user.organizationId ? (
                          <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs font-mono">{user.organizationId}</span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          (user.isActive || user.role === 'super_admin') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {(user.isActive || user.role === 'super_admin') ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm flex gap-2 items-center flex-wrap">
                        {user.role === 'pending' && (
                          <button
                            onClick={() => handleApprove(user)}
                            className="text-green-600 hover:text-green-800 font-semibold bg-green-50 px-2 py-1 rounded"
                          >
                            Approve
                          </button>
                        )}
                        {currentUser?.role === 'super_admin' && user.role !== 'super_admin' && (
                          <button
                            onClick={() => handlePromoteToSuperAdmin(user)}
                            className="text-purple-700 hover:text-purple-900 font-bold bg-purple-100 hover:bg-purple-200 px-2.5 py-1 rounded text-xs border border-purple-300 transition"
                            title="Instantly make this user a Super Admin"
                          >
                            ⭐ Promote to Super Admin
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-blue-700 hover:text-blue-900 font-semibold bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-xs border border-blue-200 transition flex items-center gap-1.5 shadow-sm"
                          title="Edit operational role, promote, or assign permissions"
                        >
                          <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit / Promote
                        </button>
                        <button
                          onClick={() => handleDelete(user._id)}
                          className="text-red-600 hover:text-red-800 font-semibold"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        ) : activeTab === 'tenants' ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold">Tenant Companies (KYC)</h2>
            </div>
            
            {organizations.length === 0 ? (
              <div className="p-6 text-center text-gray-600">No organizations registered yet</div>
            ) : (
              <div className="w-full max-w-full min-w-0 overflow-x-auto mobile-table-scroll-container rounded-b-xl">
                <table className="w-full min-w-[700px] text-left border-collapse">
                  <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Company Name</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Admin User ID</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Org ID</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Documents</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organizations.map(org => (
                      <tr key={org.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">{org.companyName}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 font-mono text-xs">{org.adminUserId}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 font-mono text-xs">{org.id}</td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex flex-col gap-2">
                            {org.documents?.cacUrl && (
                              <a href={org.documents.cacUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                  <polyline points="14 2 14 8 20 8"/>
                                </svg>
                                CAC Document
                              </a>
                            )}
                            {org.documents?.memartUrl && (
                              <a href={org.documents.memartUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                  <polyline points="14 2 14 8 20 8"/>
                                </svg>
                                MEMART
                              </a>
                            )}
                            {org.documents?.applicationUrl && (
                              <a href={org.documents.applicationUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                  <polyline points="14 2 14 8 20 8"/>
                                </svg>
                                Application
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            org.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                            org.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {org.status.toUpperCase()}
                          </span>
                          {org.status === 'active' && org.subscriptionStatus === 'defaulted' && (
                            <span className="ml-2 px-2 py-1 rounded-full text-[10px] bg-red-600 text-white font-bold animate-pulse">
                              DEFAULTED
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm flex gap-2 flex-wrap">
                          {org.status === 'pending' ? (
                            <button
                              onClick={() => handleApproveTenant(org)}
                              className="text-green-600 hover:text-green-800 font-semibold bg-green-50 px-3 py-1 rounded border border-green-200"
                            >
                              Approve Tenant
                            </button>
                          ) : (
                            <button
                              onClick={() => setManagingOrg(org)}
                              className="text-blue-600 hover:text-blue-800 font-semibold bg-blue-50 px-3 py-1 rounded border border-blue-200"
                            >
                              Manage Subs & Msg
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : activeTab === 'settings' ? (
          <div className="bg-white rounded-lg shadow overflow-hidden p-6">
            <h2 className="text-xl font-bold mb-6">System Billing Settings</h2>
            <form onSubmit={handleUpdateBankDetails} className="max-w-lg space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Bank Name</label>
                <input
                  type="text"
                  value={bankDetails.bankName}
                  onChange={(e) => setBankDetails(p => ({ ...p, bankName: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Account Name</label>
                <input
                  type="text"
                  value={bankDetails.accountName}
                  onChange={(e) => setBankDetails(p => ({ ...p, accountName: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Account Number</label>
                <input
                  type="text"
                  value={bankDetails.accountNumber}
                  onChange={(e) => setBankDetails(p => ({ ...p, accountNumber: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Transfer Instructions</label>
                <textarea
                  value={bankDetails.instructions}
                  onChange={(e) => setBankDetails(p => ({ ...p, instructions: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                  placeholder="e.g. Please use your Organization ID as the payment reference."
                />
              </div>
              <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700">
                Save Bank Details
              </button>
            </form>

            {/* Live Production & Mock Data Purge Card */}
            <div className="bg-white rounded-lg shadow p-6 border-t-4 border-red-500 mt-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <span>Environment & Data Management</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${
                      isProduction ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {isProduction ? 'Live Production Mode' : 'Sandbox Demo Mode'}
                    </span>
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 max-w-xl">
                    When onboarding your first real customers, purge all mock hardware assets, quick test credentials, and demo technicians with a single click.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPurgeModalOpen(true)}
                  className={`px-5 py-2.5 rounded-lg font-bold text-xs transition shadow flex items-center gap-2 shrink-0 ${
                    isProduction
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                >
                  {isProduction ? 'Manage Production Mode' : 'Purge All Mock Data & Go Live'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Manage Subscription Modal */}
        {managingOrg && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-xl max-w-lg w-full">
              <h3 className="text-xl font-bold mb-4 flex justify-between items-center">
                Manage {managingOrg.companyName}
                <button onClick={() => setManagingOrg(null)} className="text-gray-500 hover:text-gray-900">&times;</button>
              </h3>
              
              <div className="mb-6 p-4 bg-gray-50 border rounded-lg">
                <p className="font-semibold mb-2">Subscription Status: 
                  <span className={`ml-2 ${managingOrg.subscriptionStatus === 'defaulted' ? 'text-red-600' : 'text-green-600'}`}>
                    {(managingOrg.subscriptionStatus || 'active').toUpperCase()}
                  </span>
                </p>
                <button 
                  onClick={() => handleToggleSubscription(managingOrg)}
                  className={`px-4 py-2 rounded font-semibold text-white ${managingOrg.subscriptionStatus === 'defaulted' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {managingOrg.subscriptionStatus === 'defaulted' ? 'Reactivate Subscription' : 'Mark as Defaulted'}
                </button>
              </div>

              <form onSubmit={handleSendNotification} className="space-y-4 border-t pt-4">
                <h4 className="font-semibold text-gray-800">Send Direct Message to Master Admin</h4>
                <div>
                  <input
                    type="text"
                    placeholder="Message Title (e.g. Overdue Invoice)"
                    value={msgTitle}
                    onChange={(e) => setMsgTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <textarea
                    placeholder="Your message..."
                    value={msgBody}
                    onChange={(e) => setMsgBody(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 h-24"
                    required
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setManagingOrg(null)} className="px-4 py-2 text-gray-600 font-semibold hover:bg-gray-100 rounded">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700 flex gap-2 items-center">
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {previewImage && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-4 rounded-xl max-w-3xl w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">ID Card Verification</h3>
                <button 
                  onClick={() => setPreviewImage(null)}
                  className="text-gray-500 hover:text-gray-900 font-bold text-xl"
                >
                  &times;
                </button>
              </div>
              <img 
                src={previewImage} 
                alt="ID Card Preview" 
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg border border-gray-200"
              />
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setPreviewImage(null)}
                  className="bg-gray-800 text-white px-6 py-2 rounded-lg font-bold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Promote & Edit Staff Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-150">
            <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full border border-gray-100 overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 text-white flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <span>🛡️ Promote &amp; Edit Staff</span>
                  </h3>
                  <p className="text-blue-100 text-xs mt-0.5">Assign operational permissions, promote to leadership, or update organization</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="text-white/80 hover:text-white text-2xl font-bold p-1 leading-none rounded-lg hover:bg-white/10 transition"
                  title="Close modal"
                >
                  &times;
                </button>
              </div>

              {/* Staff Profile Bar */}
              <div className="px-6 py-4 bg-slate-50 border-b border-gray-200">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div>
                    <h4 className="font-bold text-gray-900 text-base">{editingUser.name}</h4>
                    <p className="text-gray-500 text-xs font-mono">{editingUser.email}</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase bg-blue-100 text-blue-800 border border-blue-200">
                      Current: {editingUser.role.replace(/_/g, ' ')}
                    </span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      editingUser.isActive ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-amber-100 text-amber-800 border border-amber-200'
                    }`}>
                      {editingUser.isActive ? 'Active' : 'Pending / Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleSaveEditedUser} className="p-6 space-y-4">
                {editError && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs font-semibold flex items-center gap-2">
                    <svg className="w-4 h-4 shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>{editError}</span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    Assign / Promote Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editFormData.role}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-4 py-2.5 border-2 border-blue-500 bg-blue-50/40 text-gray-900 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                  >
                    <optgroup label="⭐ Executive &amp; Platform Administration">
                      {currentUser?.role === 'super_admin' && (
                        <option value="super_admin">SUPER ADMIN (Full Platform Control)</option>
                      )}
                      <option value="master_admin">MASTER ADMIN (Tenant / Company Owner)</option>
                      <option value="admin">ADMIN (Platform Administrator)</option>
                    </optgroup>

                    <optgroup label="🏢 Field Operations &amp; Facilities Management">
                      <option value="state_manager">STATE MANAGER (Regional Operations Lead)</option>
                      <option value="site_manager">SITE MANAGER (Site Operations Lead)</option>
                      <option value="team_lead">TEAM LEAD (Field Team Leader)</option>
                      <option value="site_engineer">SITE ENGINEER (Technical &amp; Infrastructure)</option>
                      <option value="storekeeper">STOREKEEPER (Inventory &amp; Asset Custodian)</option>
                      <option value="field_staff">FIELD STAFF (Operational / On-Site)</option>
                      <option value="staff">STAFF (General Operations &amp; Office)</option>
                    </optgroup>

                    <optgroup label="🔧 Technical &amp; Forensic Hardware">
                      <option value="technician">VERIFIED TECHNICIAN (Forensic Ledger &amp; Repairs)</option>
                    </optgroup>

                    <optgroup label="💼 Commercial &amp; Customer Support">
                      <option value="sales">SALES (Business Development)</option>
                      <option value="accounts">ACCOUNTS (Finance &amp; Billing)</option>
                      <option value="customer_support">CUSTOMER SUPPORT (Helpdesk &amp; Support)</option>
                    </optgroup>

                    <optgroup label="🔒 Other Roles">
                      <option value="owner">EQUIPMENT OWNER (Asset / Device Owner)</option>
                      <option value="pending">PENDING (Awaiting Review)</option>
                    </optgroup>
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Select the target role to grant or promote this staff member into.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      Organization / Tenant ID
                    </label>
                    <input
                      type="text"
                      value={editFormData.organizationId}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, organizationId: e.target.value }))}
                      placeholder={currentUser?.organizationId || "e.g. verisure_global"}
                      className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">Leave empty to use default tenant.</p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      Account Access Status
                    </label>
                    <label className="flex items-center gap-2.5 mt-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={editFormData.isActive}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm font-semibold text-gray-800">
                        Active (Can log in &amp; perform duties)
                      </span>
                    </label>
                    <p className="text-[11px] text-gray-400 mt-1">Promoted staff will be able to access their authorized pages.</p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    Certifications (ABAC / Technical Permissions)
                  </label>
                  <input
                    type="text"
                    value={editFormData.certifications}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, certifications: e.target.value }))}
                    placeholder="e.g. electrical, telecom, hvac"
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">Comma-separated item categories authorized for this staff member.</p>
                </div>

                {/* Modal Footer Actions */}
                <div className="flex justify-between items-center pt-4 border-t border-gray-200 mt-6">
                  <div>
                    {currentUser?.role === 'super_admin' && editingUser.role !== 'super_admin' && (
                      <button
                        type="button"
                        onClick={() => handlePromoteToSuperAdmin(editingUser)}
                        className="text-purple-700 hover:text-purple-900 font-bold bg-purple-50 hover:bg-purple-100 px-3 py-2 rounded-lg text-xs border border-purple-300 transition flex items-center gap-1 shadow-sm"
                        title="Instantly grant Super Admin status"
                      >
                        ⭐ Direct Super Admin
                      </button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setEditingUser(null)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 font-semibold text-sm rounded-lg hover:bg-gray-100 transition"
                      disabled={editLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={editLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-bold text-sm shadow-md transition flex items-center gap-2 disabled:opacity-50"
                    >
                      {editLoading ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path></svg>
                          Applying Role...
                        </>
                      ) : (
                        'Save & Apply Promotion'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        <PurgeMockDataModal
          isOpen={isPurgeModalOpen}
          onClose={() => setIsPurgeModalOpen(false)}
        />
      </div>
    </div>
  );
};
